import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { checklists } from '../data/checklists';
import { ArrowLeftIcon, CheckIcon, SaveIcon, SendIcon } from 'lucide-react';
interface ChecklistDetailProps {
  checklistId: string;
  onBack: () => void;
  onSubmit?: (data: {
    checklistId: string;
    completedSteps: string[];
    duration: number;
  }) => void;
}
export function ChecklistDetail({
  checklistId,
  onBack,
  onSubmit
}: ChecklistDetailProps) {
  const {
    colors
  } = useTheme();
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [startTime] = useState(new Date());
  const [showSaveToast, setShowSaveToast] = useState(false);
  const checklist = checklists.find(c => c.id === checklistId);
  if (!checklist) {
    return <div data-id="element-71">
        <button onClick={onBack} className="flex items-center gap-2 mb-4" data-id="element-72">
          <ArrowLeftIcon size={18} data-id="element-73" />
          <span data-id="element-74">Back to Checklists</span>
        </button>
        <p data-id="element-75">Checklist not found.</p>
      </div>;
  }
  const toggleStep = (stepId: string) => {
    if (completedSteps.includes(stepId)) {
      setCompletedSteps(completedSteps.filter(id => id !== stepId));
    } else {
      setCompletedSteps([...completedSteps, stepId]);
    }
  };
  const progress = checklist.steps.length > 0 ? Math.round(completedSteps.length / checklist.steps.length * 100) : 0;
  const allStepsCompleted = completedSteps.length === checklist.steps.length;
  const handleSaveProgress = () => {
    // Save to local storage
    localStorage.setItem(`checklist-progress-${checklistId}`, JSON.stringify({
      completedSteps,
      status: 'in-progress',
      savedAt: new Date().toISOString()
    }));
    setShowSaveToast(true);
    setTimeout(() => setShowSaveToast(false), 3000);
  };
  const handleSubmit = () => {
    if (!allStepsCompleted) return;
    const endTime = new Date();
    const duration = Math.round((endTime.getTime() - startTime.getTime()) / 1000 / 60); // minutes
    // Clear saved progress
    localStorage.removeItem(`checklist-progress-${checklistId}`);
    if (onSubmit) {
      onSubmit({
        checklistId,
        completedSteps,
        duration
      });
    }
  };
  return <div className="space-y-4 pb-32" data-id="element-76">
      <button onClick={onBack} className="flex items-center gap-2 mb-2" style={{
      color: colors.accent
    }} data-id="element-77">
        <ArrowLeftIcon size={18} data-id="element-78" />
        <span data-id="element-79">Back to Checklists</span>
      </button>
      <h2 className="text-xl font-bold" data-id="element-80">{checklist.title}</h2>
      <div className="flex items-center gap-2 mb-4" data-id="element-81">
        <div className="flex-1 h-2 rounded-full overflow-hidden" style={{
        backgroundColor: colors.divider
      }} data-id="element-82">
          <div className="h-full rounded-full" style={{
          backgroundColor: colors.accent,
          width: `${progress}%`,
          transition: 'width 0.3s ease'
        }} data-id="element-83" />
        </div>
        <span className="text-sm font-medium" data-id="element-84">{progress}%</span>
      </div>
      <div className="space-y-3" data-id="element-85">
        {checklist.steps.map(step => {
        const isCompleted = completedSteps.includes(step.id);
        return <button key={step.id} onClick={() => toggleStep(step.id)} className="w-full p-4 rounded-lg text-left flex gap-3 items-center" style={{
          backgroundColor: colors.cardSurface
        }} data-id="element-86">
              <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{
            backgroundColor: isCompleted ? colors.accent : 'transparent',
            border: isCompleted ? 'none' : `1px solid ${colors.divider}`
          }} data-id="element-87">
                {isCompleted && <CheckIcon size={14} color="#fff" data-id="element-88" />}
              </div>
              <span style={{
            textDecoration: isCompleted ? 'line-through' : 'none',
            opacity: isCompleted ? 0.7 : 1
          }} data-id="element-89">
                {step.text}
              </span>
            </button>;
      })}
      </div>
      {/* Action Buttons */}
      <div className="fixed bottom-20 left-0 right-0 p-4 border-t" style={{
      backgroundColor: colors.background,
      borderColor: colors.divider
    }} data-id="element-90">
        <div className="max-w-2xl mx-auto space-y-3" data-id="element-91">
          <button onClick={handleSaveProgress} className="w-full p-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all" style={{
          backgroundColor: colors.cardSurface,
          color: colors.textPrimary,
          border: `2px solid ${colors.divider}`
        }} data-id="element-92">
            <SaveIcon size={20} data-id="element-93" />
            Save Progress
          </button>
          <button onClick={handleSubmit} disabled={!allStepsCompleted} className="w-full p-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50" style={{
          backgroundColor: allStepsCompleted ? colors.success : colors.divider,
          color: '#FFFFFF'
        }} data-id="element-94">
            <SendIcon size={20} data-id="element-95" />
            Submit Checklist
          </button>
        </div>
      </div>
      {/* Save Toast */}
      {showSaveToast && <div className="fixed bottom-40 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-full shadow-lg z-50 flex items-center gap-2" style={{
      backgroundColor: colors.success,
      color: '#FFFFFF'
    }} data-id="element-96">
          <CheckIcon size={18} data-id="element-97" />
          Checklist progress saved
        </div>}
    </div>;
}